import React from "react";
import { App } from "antd5";
import { Flex } from "styles/utility-components";

import PaywallPopover from "components/paywall/PaywallPopover";
import Button, { ButtonType } from "components/ui/button/Button";
import { useDescribeContactDetails } from "lib/hooks/api/integrations/useContactDetails";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useSendContact } from "lib/hooks/api/integrations/useSendContact";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventNames, useTracking } from "lib/tracking";
import { ContactResult } from "../../lib/hooks/api/contacts/useContacts";
import { providerIconColorMap, providerIconKeyMap } from "./ProviderButtons";
import { friendlyProviderName } from "./util";

type Props = {
  contact: ContactResult;
  showingTour: boolean;
  fullWidth?: boolean;
  buttonType?: ButtonType;
  justifyContent?: string;
};

export function SendContactToCRM({
  contact,
  showingTour,
  buttonType = "secondary",
  justifyContent,
  fullWidth,
}: Props) {
  const { message } = App.useApp();

  const { data: providerTypeResponse } = useFindIntegrationProvider();
  const providerType = providerTypeResponse?.providerType;
  const { data: extraContactData } = useDescribeContactDetails(contact.id, {
    enabled: !!providerType,
  });
  const { logEvent } = useTracking();

  const { checkSubscription, authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": "CRM button",
  });

  // Disabling contacts CTAs like viewing or sending contacts via CRM.
  // Not disabling connect to CRM when user doesn't have contacts because we always
  // want them to do this (unless there's an ongoing tour)
  const disableContactsCta = !hasContacts || showingTour || !!window.guestUser;

  const trackingAttributes = {
    "Connected provider": providerType,
    "Data types": "Contact",
    "Contact function": contact.jobFunction,
    "Contact job title": contact.jobTitle,
    "Contact seniority": contact.seniority,
    "Buyer name": contact.buyer?.name,
  };

  const { mutate: sendContact, isLoading } = useSendContact({
    onSuccess: (data, _variables, _context) => {
      if (!providerType) {
        return;
      }
      logEvent(EventNames.objectSentToCRM, trackingAttributes);
      message.open({
        type: data.isDuplicate ? "warning" : "success",
        content: (
          <Flex gap={8} alignItems="center">
            {data.isDuplicate
              ? `Contact already exists in your ${friendlyProviderName[providerType]} account`
              : `Sent to ${friendlyProviderName[providerType]} `}

            {data.externalUrl && (
              <Button
                as="a"
                buttonType="secondary"
                href={data.externalUrl}
                target="new-tab"
                icon={providerIconKeyMap[providerType]}
                iconProps={{
                  color: providerIconColorMap[providerType],
                }}
              >
                View
              </Button>
            )}
          </Flex>
        ),
      });
    },
    onError() {
      void message.error("Failed to send contact");
    },
  });

  if (!providerType) return null;

  if (extraContactData?.externalUrl) {
    return (
      <Button
        as="a"
        buttonType={buttonType}
        href={disableContactsCta ? "" : extraContactData.externalUrl}
        onClick={(e: React.MouseEvent) => {
          if (showingTour) {
            e.preventDefault();
          } else {
            checkSubscription();
          }
        }}
        target="new-tab"
        icon={providerIconKeyMap[providerType]}
        iconProps={{
          color: providerIconColorMap[providerType],
        }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
      >
        View
      </Button>
    );
  }

  return (
    <PaywallPopover
      featureType={window.guestUser ? "GUEST_ACCESS_UPGRADE" : "CONTACTS"}
      contextSource="CRM button"
    >
      <Button
        as="button"
        buttonType={buttonType}
        onClick={() => {
          if (!disableContactsCta) {
            logEvent(EventNames.sendToCRMClicked, trackingAttributes);
            sendContact({
              salutation: contact.title,
              firstName: contact.firstName,
              contactCompanyName: contact.buyer?.name || "",
              jobTitle: contact.jobTitle,
              jobFunction: contact.jobFunction,
              seniority: contact.seniority,
              lastName: contact.lastName,
              contactId: contact.id,
              emailAddress: contact.email || "",
              phoneNumber: contact.phone || "",
            });
          }
        }}
        disabled={isLoading}
        icon={providerIconKeyMap[providerType]}
        iconProps={{
          color: providerIconColorMap[providerType],
        }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
      >
        Send
      </Button>
    </PaywallPopover>
  );
}
